import { dictionaryApi } from './dictionaryClient';
import { normalizeBarna, type Barna } from './barnaApi';

export type AssociatedBarna = {
  barnaId: number;
  includedBhedas: number[];
  /** Join-row id when API returns expanded associated_barnas. */
  associationId?: number;
  barna?: Barna | null;
};

export type Pratyahar = {
  id: number;
  name: string;
  synonyms: string[];
  associatedBarnas: AssociatedBarna[];
};

export type CreatePratyaharBody = {
  name: string;
  synonyms: string[];
  associated_barnas: Array<{ barna_id: number; included_bhedas: number[] }>;
};

export type UpdatePratyaharBody = Partial<CreatePratyaharBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function toStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

export function normalizeAssociatedBarna(raw: unknown): AssociatedBarna | null {
  if (!isRecord(raw)) return null;
  const barnaRaw = raw.barna;
  const barna = barnaRaw ? normalizeBarna(barnaRaw) : null;
  let barnaId = toNum(raw.barnaId ?? raw.barna_id);
  if (barnaId == null && barna) barnaId = barna.id;
  if (barnaId == null) return null;
  const arr = raw.includedBhedas ?? raw.included_bhedas;
  const includedBhedas = Array.isArray(arr)
    ? arr.map((x) => toNum(x)).filter((n): n is number => n != null)
    : [];
  const associationId = toNum(raw.id) ?? undefined;
  return { barnaId, includedBhedas, associationId, barna };
}

/** Prefer nested barna from API, then lookup table. */
export function associatedBarnaLabel(
  barnaId: number,
  assoc: AssociatedBarna | undefined,
  barnaRows: Array<Pick<Barna, 'id' | 'name'>>
): string {
  if (assoc?.barna?.name) return `${assoc.barna.name} (#${barnaId})`;
  const b = barnaRows.find((x) => x.id === barnaId);
  return b ? `${b.name} (#${barnaId})` : `#${barnaId}`;
}

export function normalizePratyahar(raw: unknown): Pratyahar | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  if (id == null) return null;
  const synRaw = raw.synonyms;
  const synonyms = Array.isArray(synRaw) ? synRaw.map((x) => toStr(x)) : [];
  const assocRaw = raw.associatedBarnas ?? raw.associated_barnas;
  const associatedBarnas = Array.isArray(assocRaw)
    ? assocRaw.map(normalizeAssociatedBarna).filter((x): x is AssociatedBarna => x != null)
    : [];
  return {
    id,
    name: toStr(raw.name),
    synonyms,
    associatedBarnas,
  };
}

function normalizeList(data: unknown): Pratyahar[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizePratyahar).filter((x): x is Pratyahar => x != null);
}

export async function listPratyahar(): Promise<Pratyahar[]> {
  const res = await dictionaryApi.get<unknown>('/pratyahar');
  return normalizeList(res.data);
}

export async function getPratyahar(id: number): Promise<Pratyahar> {
  const res = await dictionaryApi.get<unknown>(`/pratyahar/${id}`);
  const row = normalizePratyahar(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid pratyahar response');
  return row;
}

export async function createPratyahar(body: CreatePratyaharBody): Promise<Pratyahar> {
  const res = await dictionaryApi.post<unknown>('/pratyahar', body);
  const row = normalizePratyahar(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid pratyahar response');
  return row;
}

export async function updatePratyahar(id: number, body: UpdatePratyaharBody): Promise<Pratyahar> {
  const res = await dictionaryApi.patch<unknown>(`/pratyahar/${id}`, body);
  const row = normalizePratyahar(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid pratyahar response');
  return row;
}

export async function deletePratyahar(id: number): Promise<void> {
  await dictionaryApi.delete(`/pratyahar/${id}`);
}

/** API request shape for associated rows (snake_case). */
export function associatedBarnasToBody(rows: AssociatedBarna[]): Array<{ barna_id: number; included_bhedas: number[] }> {
  return rows.map((r) => ({
    barna_id: r.barnaId,
    included_bhedas: [...r.includedBhedas],
  }));
}

function synonymsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function associatedEqual(a: AssociatedBarna[], b: AssociatedBarna[]): boolean {
  if (a.length !== b.length) return false;
  const norm = (rows: AssociatedBarna[]) =>
    [...rows]
      .map((r) => ({
        barnaId: r.barnaId,
        includedBhedas: [...r.includedBhedas].sort((x, y) => x - y),
      }))
      .sort((x, y) => x.barnaId - y.barnaId);
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

export function buildPratyaharPatch(before: Pratyahar, after: Pratyahar): UpdatePratyaharBody {
  const patch: UpdatePratyaharBody = {};
  if (after.name !== before.name) patch.name = after.name;
  if (!synonymsEqual(after.synonyms, before.synonyms)) patch.synonyms = after.synonyms;
  if (!associatedEqual(after.associatedBarnas, before.associatedBarnas)) {
    patch.associated_barnas = associatedBarnasToBody(after.associatedBarnas);
  }
  return patch;
}
